import { Logger } from '@calendly/logging-service'
import {
  initialize,
  statsig,
  type InitializeProps,
  type StatsigUserInput,
} from '@calendly/statsig'

import Settings from 'core/settings'

import { buildStatsigUser, type StatsigReduxState } from './statsig_user'

export type { StatsigReduxState } from './statsig_user'

/** Narrow read-only shape for `Settings.statsig`; core/settings is untyped at runtime. */
interface StatsigAppSettings {
  client_key?: string
  clientKey?: string
  environment_tier?: string
}

type SettingsWithStatsig = typeof Settings & { statsig?: StatsigAppSettings }

const statsigSettingsBlock = (): StatsigAppSettings | undefined => (Settings as SettingsWithStatsig).statsig

type StatsigClientFacade = {
  checkGate: typeof statsig.checkGate
  getDynamicConfig: typeof statsig.getDynamicConfig
  getFeatureGate: typeof statsig.getFeatureGate
}

let clientFacade: StatsigClientFacade | undefined

/** Client SDK key from app settings; empty / whitespace-only values are treated as unset. */
const clientKeyFromSettings = (): string => {
  const s = statsigSettingsBlock()
  const raw = s?.client_key ?? s?.clientKey
  if (raw == null) return ''
  const trimmed = String(raw).trim()
  return trimmed
}

const environmentTierFromSettings = (): string | undefined => {
  const s = statsigSettingsBlock()
  return s?.environment_tier
}

const executeStatsigInitialize = async (
  userID: string | undefined,
  clientKey: string,
  custom: NonNullable<StatsigUserInput['custom']>,
  environmentTier: string | undefined,
  orgIdForCustomIds: string | undefined
): Promise<void> => {
  Logger.debug('[statsig] creating client and starting initialize', {
    hasUserId: userID != null && userID !== '',
    environmentTier,
    customFieldCount: Object.keys(custom).length,
  })
  const user: StatsigUserInput = {
    userID,
    custom,
    ...(orgIdForCustomIds != null && orgIdForCustomIds !== ''
      ? { customIDs: { org_id: orgIdForCustomIds } }
      : {}),
  }
  const initPayload: InitializeProps = {
    clientKey,
    user,
    ...(environmentTier ? { environment: { tier: environmentTier } } : {}),
    overrides: [],
  }
  await initialize(initPayload)
  clientFacade = {
    checkGate: (gateName, options) => statsig.checkGate(gateName, options),
    getDynamicConfig: (configName, options) => statsig.getDynamicConfig(configName, options),
    getFeatureGate: (gateName, options) => statsig.getFeatureGate(gateName, options),
  }
  Logger.debug('[statsig] initialize finished; client ready')
}

export const getStatsigClient = (): StatsigClientFacade | undefined => clientFacade

/**
 * Starts Statsig initialization without blocking the caller.
 * User fields (from segment `identify_options` traits / Optimizely) come from `store.getState()`.
 * Gate overrides mirror `bootstrapData.enabled_features` (`'on'` → true).
 */
export const startStatsig = (
  store: { getState: () => StatsigReduxState }
): void => {
  const clientKey = clientKeyFromSettings()
  if (!clientKey) {
    Logger.debug('[statsig] startStatsig skipped (no Settings.statsig.client_key)')
    return
  }

  const environmentTier = environmentTierFromSettings()

  Logger.debug('[statsig] startStatsig scheduling initialization', {
    hasClientKey: true,
    environmentTier,
  })

  const runBootstrap = async (): Promise<void> => {
    try {
      const state = store.getState()
      const { userID, custom, orgIdForCustomIds: orgId } = buildStatsigUser(state)
      await executeStatsigInitialize(
        userID,
        clientKey,
        custom,
        environmentTier,
        orgId
      )
    } catch {
      Logger.warn({ message: '[statsig] initialization failed' } as any)
    }
  }
  runBootstrap().catch(() => { })
}
